/**
 * Luvè set builder (sections/luve-trio-builder.liquid): choose a set of trees, each in its own height and colour, or
 * start from a ready-made set. The trees are drawn to scale together in their real shapes (the Live Tree geometry:
 * a tabletop Forma is a broad cone, a floor Forma a slim spire, Élan a stack of rings), beside a 165cm person on the
 * floor or a 22cm wine glass on a table.
 *
 * One tree is edited at a time, so the builder stays short on a phone: tap a tree in the drawing or in the row of
 * trees under it, then choose its tree, colour and height with large chips (no dropdowns, every target thumb-sized).
 * "Same colour for every tree" recolours the whole set in one tap. The total, the saving, the free shipping line and
 * the instalments follow every change; on a phone the total and the button stay pinned to the bottom of the screen
 * while the builder is in view, and stand in for the product page's own sticky bar. One button adds the whole set,
 * through Horizon's own batch add, so the cart drawer opens and the cart count updates as for any other add.
 */
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { tierPaths, halfFor, finsFor } from '@theme/luve-live-tree';

const SVG_NS = 'http://www.w3.org/2000/svg';
const ADDED_MS = 1800;
// Trees this tall or shorter stand on a dining table; a set of only those is drawn on one
const TABLE_MAX = 60;
const PERSON_CM = 165;
const GLASS_CM = 22;
// Pleats never closer than this on screen, so a small tree stays crisp
const MIN_PLEAT_PX = 3.5;

/** A$ always, no cents on whole dollars: A$1,097, A$19.95 */
const money = (cents) =>
  `A$${(cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;

const r2 = (n) => Math.round(n * 100) / 100;

/**
 * @param {string} name
 * @param {Record<string, string | number>} [attributes]
 * @param {Element} [parent]
 */
function svg(name, attributes = {}, parent) {
  const node = document.createElementNS(SVG_NS, name);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
  parent?.append(node);
  return node;
}

/**
 * @param {string} tag
 * @param {Record<string, string>} [attributes]
 * @param {string} [text]
 */
function html(tag, attributes = {}, text) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value);
  if (text !== undefined) node.textContent = text;
  return node;
}

/** "Burgundy and Off white", "Sage, Burgundy and Off white" @param {string[]} items */
const listOf = (items) => (items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`);

/**
 * @typedef {{ id: number, colour: string, cm: number, price: number, available: boolean }} Variant
 * @typedef {{ name: string, tones: string[] }} Colour
 * @typedef {{ model: string, title: string, url: string, colours: Colour[], variants: Variant[] }} Product
 * @typedef {{ name: string, note: string, model: string, sizes: number[], colours: string[] }} Preset
 * @typedef {{ model: string, colour: string, cm: number }} Slot
 */

class LuveTrioBuilder extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;
  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {Product[]} */
  #products = [];
  /** @type {Preset[]} */
  #presets = [];
  /** @type {{ enabled?: boolean, name?: string, count?: number, min?: number, max?: number }} */
  #afterpay = {};
  /** The bundle saving tiers, fewest trees first: two trees save 5%, three 10%... @type {{ min: number, pct: number }[]} */
  #tiers = [];
  #savingLabel = 'Set saving';
  /** @type {Slot[]} */
  #slots = [];
  /** The tree being edited */
  #selected = 0;
  /** A colour chosen for one tree goes to every tree */
  #same = true;
  /** The ready-made set the trees still match, -1 once changed */
  #active = -1;
  /** Trees to unfold into place on the next drawing */
  #fresh = new Set();
  #busy = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #addedTimer;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    let data = {};
    try {
      data = JSON.parse(this.querySelector('[data-builder-data]')?.textContent || '{}');
    } catch {
      data = {};
    }
    this.#products = (data.products || []).filter((product) => product && product.variants?.length);
    this.#afterpay = data.afterpay || {};
    if (data.bundle?.enabled) {
      this.#tiers = String(data.bundle.tiers || '')
        .split(',')
        .map((tier) => tier.split(':').map((part) => parseFloat(part)))
        .filter(([min, pct]) => min > 0 && pct > 0)
        .map(([min, pct]) => ({ min, pct }))
        .sort((a, b) => a.min - b.min);
      this.#savingLabel = data.bundle.label || this.#savingLabel;
    }
    this.#presets = (data.presets || []).map((preset) => this.#normalise(preset)).filter(Boolean);
    if (!this.#products.length) return;

    this.#renderPresets();
    // On a product page, start from the first set of that page's tree (the Élan Pair on Élan)
    const pageModel = this.dataset.pageModel;
    const start = Math.max(0, pageModel ? this.#presets.findIndex((preset) => preset.model === pageModel) : 0);
    if (this.#presets.length) this.#applyPreset(start);
    else {
      const forma = this.#product('forma') || this.#products[0];
      const colour = this.#colourFor(forma.model, 'Sage');
      this.#slots = [{ model: forma.model, colour, cm: this.#sizes(forma.model, colour)[0] }];
      this.#render();
    }

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    // Links to the set builder ("Build your set", "Or choose your own trees") stay on this page when it has one
    document.addEventListener(
      'click',
      (event) => {
        const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
        const href = link?.getAttribute('href') || '';
        if (!/#(corner-trio|build-your-set)$/.test(href) || this.contains(link)) return;
        event.preventDefault();
        const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        // The whole section, its label and heading too, stopping under the sticky header (scroll-margin-top)
        (this.closest('.luve-builder-section') || this).scrollIntoView({ behavior: smooth, block: 'start' });
      },
      { signal }
    );
    window.addEventListener('resize', () => this.#renderScene(), { signal, passive: true });
    this.#watchView();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#addedTimer);
  }

  /**
   * "In view" while the builder is on screen above its pinned bar: the product page's own sticky add to cart steps
   * aside then (sections/luve-trio-builder.liquid), so two bars never stack at the bottom of a phone.
   */
  #watchView() {
    if (!('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      ([entry]) => this.classList.toggle('is-in-view', Boolean(entry?.isIntersecting)),
      { rootMargin: '0px 0px -96px 0px' }
    );
    this.#observer.observe(this);
  }

  get #max() {
    return Math.max(2, Number(this.dataset.max) || 4);
  }

  get #uid() {
    return this.dataset.uid || 'builder';
  }

  /** @param {string} model */
  #product(model) {
    return this.#products.find((product) => product.model === model);
  }

  /** Heights that exist for a model in a colour, tallest first. @param {string} model @param {string} colour */
  #sizes(model, colour) {
    const product = this.#product(model);
    if (!product) return [];
    return [...new Set(product.variants.filter((variant) => variant.colour === colour).map((variant) => variant.cm))].sort(
      (a, b) => b - a
    );
  }

  /** @param {Slot} slot */
  #variant(slot) {
    return this.#product(slot.model)?.variants.find(
      (variant) => variant.colour === slot.colour && variant.cm === slot.cm
    );
  }

  /** The colour a tree of this model takes: the one wanted when the model is made in it, else its first. */
  #colourFor(model, wanted = '') {
    const product = this.#product(model);
    if (!product) return '';
    return product.colours.find((colour) => colour.name === wanted)?.name || product.colours[0]?.name || '';
  }

  /** @param {string} model @param {string} colour @param {number} cm */
  #nearest(model, colour, cm) {
    const sizes = this.#sizes(model, colour);
    if (!sizes.length) return cm;
    return sizes.reduce((best, size) => (Math.abs(size - cm) < Math.abs(best - cm) ? size : best), sizes[0]);
  }

  /** @param {Slot} tree */
  #tone(tree) {
    return this.#product(tree.model)?.colours.find((colour) => colour.name === tree.colour)?.tones || [];
  }

  /**
   * A ready-made set from the section's blocks, or null when a tree in it does not exist (the Table Trio waits for
   * the 45cm Forma).
   *
   * @param {any} preset
   * @returns {Preset | null}
   */
  #normalise(preset) {
    const model = preset?.model === 'elan' ? 'elan' : 'forma';
    const product = this.#product(model);
    if (!product) return null;
    const sizes = (preset.sizes || []).map((size) => parseInt(String(size), 10)).filter((size) => size > 0);
    const colours = (preset.colours || []).map((colour) => String(colour).trim()).filter(Boolean);
    if (!sizes.length || sizes.length > this.#max) return null;
    const exists = sizes.every((cm, i) => {
      const colour = colours[i];
      return product.variants.some((variant) => variant.cm === cm && (!colour || variant.colour === colour));
    });
    if (!exists) return null;
    return { name: String(preset.name || ''), note: String(preset.note || ''), model, sizes, colours };
  }

  /** @param {number} index */
  #applyPreset(index) {
    const preset = this.#presets[index];
    if (!preset) return;
    // A set in one colour keeps the colour already chosen (Burgundy stays Burgundy); Sage to begin with
    const current = this.#same ? this.#slots[this.#selected]?.colour || 'Sage' : 'Sage';
    this.#slots = preset.sizes.map((cm, i) => {
      const colour = this.#colourFor(preset.model, preset.colours[i] || current);
      // A size missing in this colour falls back to the nearest one that exists
      return { model: preset.model, colour, cm: this.#nearest(preset.model, colour, cm) };
    });
    this.#same = this.#slots.every((tree) => tree.colour === this.#slots[0]?.colour);
    this.#selected = 0;
    this.#active = index;
    this.#fresh = new Set(this.#slots.map((_, i) => i));
    this.#render();
  }

  /** Every tree in this colour, where its model is made in it. @param {string} colour */
  #recolour(colour) {
    this.#slots = this.#slots.map((tree) => {
      const next = this.#colourFor(tree.model, colour);
      if (next !== colour) return tree;
      return { ...tree, colour: next, cm: this.#nearest(tree.model, next, tree.cm) };
    });
    this.#fresh = new Set(this.#slots.map((_, i) => i));
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const tree = this.#slots[this.#selected];
    if (!tree) return;

    switch (target.dataset.field) {
      case 'same':
        this.#same = target.checked;
        if (this.#same) this.#recolour(tree.colour);
        else this.#fresh = new Set();
        break;
      case 'colour':
        if (this.#same) this.#recolour(target.value);
        else {
          tree.colour = target.value;
          tree.cm = this.#nearest(tree.model, tree.colour, tree.cm);
          this.#fresh = new Set([this.#selected]);
        }
        break;
      case 'cm':
        tree.cm = Number(target.value);
        this.#fresh = new Set([this.#selected]);
        break;
      case 'model':
        tree.model = target.value;
        tree.colour = this.#colourFor(tree.model, tree.colour);
        tree.cm = this.#nearest(tree.model, tree.colour, tree.cm);
        this.#fresh = new Set([this.#selected]);
        break;
      default:
        return;
    }

    this.#active = -1;
    this.#render();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button, [data-tree]') : null;
    if (!target || !this.contains(target)) return;
    const data = target instanceof HTMLElement || target instanceof SVGElement ? target.dataset : {};

    if (data.preset !== undefined) {
      this.#applyPreset(Number(data.preset));
    } else if (data.select !== undefined) {
      this.#select(Number(data.select));
    } else if (data.tree !== undefined) {
      // A tree tapped in the drawing: edit it, and show its controls if they are off screen
      this.#select(Number(data.tree));
      this.querySelector('[data-builder-tabs]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } else if (data.remove !== undefined) {
      this.#remove(Number(data.remove));
    } else if (target.hasAttribute('data-builder-more')) {
      this.#addTree();
    } else if (target.hasAttribute('data-builder-add')) {
      this.#onAdd();
    }
  };

  /** The row of trees is a tab list: arrows, Home and End move between trees @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = event.target instanceof HTMLElement ? event.target.closest('[role="tab"]') : null;
    if (!tab) return;
    const last = this.#slots.length - 1;
    const moves = { ArrowRight: this.#selected + 1, ArrowLeft: this.#selected - 1, Home: 0, End: last };
    if (!(event.key in moves)) return;
    event.preventDefault();
    const next = (moves[/** @type {keyof typeof moves} */ (event.key)] + last + 1) % (last + 1);
    this.#select(next);
    /** @type {HTMLElement | null} */ (this.querySelector(`[data-select="${next}"]`))?.focus();
  };

  /** @param {number} index */
  #select(index) {
    if (!this.#slots[index] || index === this.#selected) return;
    this.#selected = index;
    this.#fresh = new Set();
    this.#render();
  }

  /** @param {number} index */
  #remove(index) {
    if (this.#slots.length <= 1 || !this.#slots[index]) return;
    this.#slots.splice(index, 1);
    this.#selected = Math.min(index, this.#slots.length - 1);
    this.#active = -1;
    this.#fresh = new Set();
    this.#render();
    /** @type {HTMLElement | null} */ (this.querySelector(`[data-select="${this.#selected}"]`))?.focus();
  }

  /** Another tree like the last one, a size down: sets read best stepping down in height */
  #addTree() {
    if (this.#slots.length >= this.#max) return;
    const last = this.#slots[this.#slots.length - 1];
    const model = last?.model || this.#products[0].model;
    const colour = last?.colour || this.#colourFor(model, 'Sage');
    const sizes = this.#sizes(model, colour);
    const smaller = sizes.find((size) => size < (last?.cm ?? Infinity));
    this.#slots.push({ model, colour, cm: smaller ?? sizes[sizes.length - 1] ?? sizes[0] });
    this.#selected = this.#slots.length - 1;
    this.#active = -1;
    this.#fresh = new Set([this.#selected]);
    this.#render();
  }

  #render() {
    const focus = this.#focusKey();
    this.#renderPresetState();
    this.#renderTabs();
    this.#renderEditor();
    this.#renderScene();
    this.#renderSummary();
    if (focus) /** @type {HTMLElement | null} */ (this.querySelector(focus))?.focus({ preventScroll: true });
  }

  /** A selector for the control that has focus, to give it back after the controls are drawn again */
  #focusKey() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) return '';
    if (active instanceof HTMLInputElement && active.dataset.field) {
      return active.type === 'checkbox'
        ? `[data-field="${active.dataset.field}"]`
        : `[data-field="${active.dataset.field}"][value="${CSS.escape(active.value)}"]`;
    }
    if (active.dataset.select !== undefined) return `[data-select="${this.#selected}"]`;
    if (active.dataset.preset !== undefined) return `[data-preset="${active.dataset.preset}"]`;
    return '';
  }

  #renderPresets() {
    const holder = this.querySelector('[data-builder-presets]');
    if (!holder) return;
    holder.replaceChildren();
    /** @type {HTMLElement} */ (holder).hidden = !this.#presets.length;
    this.#presets.forEach((preset, i) => {
      const button = html('button', { type: 'button', class: 'luve-builder__preset', 'data-preset': String(i), 'aria-pressed': 'false' });
      button.append(html('span', { class: 'luve-builder__preset-name' }, preset.name));
      button.append(
        html('span', { class: 'luve-builder__preset-note' }, preset.note || `${preset.sizes.join(' · ')}cm`)
      );
      holder.append(button);
    });
  }

  #renderPresetState() {
    for (const button of this.querySelectorAll('[data-preset]')) {
      button.setAttribute('aria-pressed', String(Number(button.getAttribute('data-preset')) === this.#active));
    }
  }

  /** The trees in the set, in the drawing's order, as tabs: a colour dot and a height each */
  #renderTabs() {
    const tabs = this.querySelector('[data-builder-tabs]');
    if (!tabs) return;
    tabs.replaceChildren();

    this.#slots.forEach((tree, i) => {
      const product = this.#product(tree.model);
      const selected = i === this.#selected;
      const tab = html('button', {
        type: 'button',
        role: 'tab',
        id: `${this.#uid}-tab-${i}`,
        class: 'luve-builder__tab',
        'data-select': String(i),
        'aria-selected': String(selected),
        'aria-controls': `${this.#uid}-editor`,
        'aria-label': `Tree ${i + 1}: ${product?.title || ''}, ${tree.colour}, ${tree.cm}cm`,
        tabindex: selected ? '0' : '-1',
      });
      // A small tree in its colour, pointed for Forma and rounded for Élan, so a mixed set reads at a glance
      const icon = svg('svg', { viewBox: '0 0 14 16', class: 'luve-builder__tab-icon', 'aria-hidden': 'true', focusable: 'false' });
      svg('path', { d: tree.model === 'elan' ? 'M7 1C10 1 12.5 7 12.5 15H1.5C1.5 7 4 1 7 1Z' : 'M7 1L12.5 15H1.5Z' }, icon);
      /** @type {SVGElement} */ (icon).style.setProperty('--luve-builder-dot', this.#tone(tree)[0] || '#b5aa9b');
      tab.append(icon, html('span', { 'aria-hidden': 'true' }, `${tree.cm}cm`));
      tabs.append(tab);
    });

    for (const more of this.querySelectorAll('[data-builder-more]')) {
      /** @type {HTMLElement} */ (more).hidden = this.#slots.length >= this.#max;
    }
  }

  /**
   * @param {string} legend
   * @param {string} className - the options' layout
   */
  #group(legend, className) {
    const fieldset = html('fieldset', { class: 'luve-builder__group' });
    fieldset.append(html('legend', { class: 'luve-builder__legend' }, legend));
    const options = html('div', { class: className });
    fieldset.append(options);
    return { fieldset, options };
  }

  /** Lay the choices out in even rows: all in one row up to four, else rows of three @param {HTMLElement} options */
  #columns(options) {
    const count = options.children.length;
    options.style.setProperty('--luve-builder-cols', String(count <= 4 ? Math.max(count, 2) : 3));
  }

  /**
   * A radio drawn as a chip: the input covers it, so a tap anywhere on the chip chooses it.
   *
   * @param {{ name: string, value: string, field: string, checked: boolean, disabled?: boolean, className: string, main: string, sub?: string, dot?: string }} choice
   */
  #choice({ name, value, field, checked, disabled = false, className, main, sub, dot }) {
    const label = html('label', { class: className });
    const input = /** @type {HTMLInputElement} */ (html('input', { type: 'radio', name, value }));
    input.dataset.field = field;
    input.checked = checked;
    input.disabled = disabled;
    label.append(input);
    if (dot) {
      const swatch = html('span', { class: 'luve-builder__dot', 'aria-hidden': 'true' });
      swatch.style.setProperty('--luve-builder-dot', dot);
      label.append(swatch);
    }
    label.append(html('span', { class: 'luve-builder__choice-main' }, main));
    if (sub) label.append(html('span', { class: 'luve-builder__choice-sub' }, sub));
    return label;
  }

  /** The selected tree's controls: which tree, its colour, its height */
  #renderEditor() {
    const editor = this.querySelector('[data-builder-editor]');
    if (!editor) return;
    editor.replaceChildren();
    const i = this.#selected;
    const tree = this.#slots[i];
    const product = tree && this.#product(tree.model);
    if (!tree || !product) return;
    editor.setAttribute('aria-labelledby', `${this.#uid}-tab-${i}`);
    const several = this.#slots.length > 1;

    const head = html('div', { class: 'luve-builder__editor-head' });
    head.append(html('p', { class: 'luve-builder__editor-title' }, several ? `Tree ${i + 1} of ${this.#slots.length}` : 'Your tree'));
    const variant = this.#variant(tree);
    head.append(html('p', { class: 'luve-builder__editor-price' }, variant ? money(variant.price) : ''));
    if (several) {
      head.append(html('button', { type: 'button', class: 'luve-builder__remove', 'data-remove': String(i), 'aria-label': `Remove tree ${i + 1}` }, 'Remove'));
    }
    editor.append(head);

    if (this.#products.length > 1) {
      const { fieldset, options } = this.#group('Tree', 'luve-builder__segments');
      for (const option of this.#products) {
        options.append(
          this.#choice({ name: `${this.#uid}-model`, value: option.model, field: 'model', checked: option.model === tree.model, className: 'luve-builder__segment', main: option.title })
        );
      }
      editor.append(fieldset);
    }

    const { fieldset: colourSet, options: colours } = this.#group('Colour', 'luve-builder__pills');
    for (const colour of product.colours) {
      if (!this.#sizes(tree.model, colour.name).length) continue;
      colours.append(
        this.#choice({ name: `${this.#uid}-colour`, value: colour.name, field: 'colour', checked: colour.name === tree.colour, className: 'luve-builder__pill', main: colour.name, dot: colour.tones[0] || '#b5aa9b' })
      );
    }
    this.#columns(colours);
    if (several) {
      const same = html('label', { class: 'luve-builder__same' });
      const box = /** @type {HTMLInputElement} */ (html('input', { type: 'checkbox' }));
      box.dataset.field = 'same';
      box.checked = this.#same;
      same.append(box, html('span', {}, 'Same colour for every tree'));
      colourSet.append(same);
      // A tree whose model is not made in the set's colour keeps its own: say why
      if (this.#same) {
        const apart = [...new Set(this.#slots.filter((other) => other.colour !== tree.colour).map((other) => other.model))]
          .map((model) => this.#product(model))
          .filter((other) => other && !other.colours.some((colour) => colour.name === tree.colour));
        for (const other of apart) {
          if (!other) continue;
          colourSet.append(html('p', { class: 'luve-builder__hint' }, `${other.title} comes in ${listOf(other.colours.map((colour) => colour.name))}.`));
        }
      }
    }
    editor.append(colourSet);

    const { fieldset: heightSet, options: heights } = this.#group('Height', 'luve-builder__chips');
    for (const cm of this.#sizes(tree.model, tree.colour).slice().reverse()) {
      const option = this.#variant({ ...tree, cm });
      const available = Boolean(option && option.available);
      heights.append(
        this.#choice({
          name: `${this.#uid}-height`,
          value: String(cm),
          field: 'cm',
          checked: cm === tree.cm,
          disabled: !available,
          className: 'luve-builder__chip',
          main: `${cm}cm`,
          sub: available && option ? money(option.price) : 'Not available',
        })
      );
    }
    this.#columns(heights);
    editor.append(heightSet);
  }

  /** The set drawn to scale, side by side on the floor beside a person, or on a table beside a wine glass */
  #renderScene() {
    const drawing = this.querySelector('[data-builder-drawing]');
    const scene = this.querySelector('[data-builder-scene]');
    if (!drawing || !scene || !this.#slots.length) return;
    scene.replaceChildren();

    const slots = this.#slots;
    const table = slots.every((tree) => tree.cm <= TABLE_MAX);
    const halves = slots.map((tree) => halfFor(tree.model, tree.cm) * tree.cm);
    const tallest = Math.max(...slots.map((tree) => tree.cm), table ? GLASS_CM : PERSON_CM);
    const gap = Math.max(2, tallest * 0.04);

    const xs = [];
    let x = 0;
    slots.forEach((_, i) => {
      if (i) x += halves[i - 1] + gap + halves[i];
      xs.push(x);
    });
    const left = xs[0] - halves[0];
    const reference = table ? 7 : 23;
    const referenceX = xs[xs.length - 1] + halves[halves.length - 1] + gap * 2.2 + reference;
    const right = referenceX + reference;
    const shift = -(left + right) / 2;

    // Frame: the whole set and its reference, with room for the labels, in the stage's own shape
    const pad = tallest * 0.08;
    const box = drawing.getBoundingClientRect();
    const ratio = box.width && box.height ? box.width / box.height : 1.25;
    /** @param {number} size - the labels' size, in cm of the drawing */
    const frame = (size) => {
      const tall = tallest + pad + size * 2.9;
      return { height: tall, width: Math.max(right - left + pad * 2, tall * ratio) };
    };
    let labelSize = tallest * 0.05;
    let { height, width } = frame(labelSize);
    // Labels about 11px on screen at the least, so a phone can read them
    if (box.width && (11 * width) / box.width > labelSize) {
      labelSize = (11 * width) / box.width;
      ({ height, width } = frame(labelSize));
    }
    drawing.setAttribute('viewBox', `${r2(-width / 2)} ${r2(-(tallest + pad))} ${r2(width)} ${r2(height)}`);
    const pixelsPerCm = box.width ? box.width / width : 2;

    // The floor, or the table top
    const floorLeft = -width / 2;
    if (table) {
      svg('rect', { x: r2(floorLeft), y: 0, width: r2(width), height: r2(tallest * 0.04), class: 'luve-builder__table' }, scene);
    } else {
      svg('line', { x1: r2(floorLeft), y1: 0, x2: r2(width / 2), y2: 0, class: 'luve-builder__floor' }, scene);
    }

    slots.forEach((tree, i) => {
      const cx = xs[i] + shift;
      const selected = i === this.#selected && slots.length > 1;
      const group = svg('g', { class: `luve-builder__tree${selected ? ' is-selected' : ''}`, 'data-tree': String(i) }, scene);
      // The whole slot answers a tap, not only the paper: a 30cm tree beside a 240cm one is a small target
      svg('rect', { x: r2(cx - halves[i] - gap / 2), y: r2(-tree.cm - pad * 0.5), width: r2(halves[i] * 2 + gap), height: r2(tree.cm + pad * 0.5 + labelSize * 2.6), class: 'luve-builder__hit' }, group);
      this.#drawTree(group, tree, this.#tone(tree), cx, i, pixelsPerCm);
      const text = svg('text', { x: r2(cx), y: r2(labelSize * 1.9), 'text-anchor': 'middle', 'font-size': r2(labelSize), class: 'luve-builder__label' }, group);
      text.textContent = `${tree.cm}cm`;
      if (selected) {
        svg('rect', { x: r2(cx - labelSize * 1.4), y: r2(labelSize * 2.35), width: r2(labelSize * 2.8), height: r2(labelSize * 0.16), class: 'luve-builder__mark' }, group);
      }
    });

    if (table) {
      // The Live Tree's 22cm wine glass, its foot at the table top
      const glass = svg('g', { class: 'luve-builder__glass', transform: `translate(${r2(referenceX + shift - 258)} -243)` }, scene);
      svg('path', { d: 'M252 221 Q252 233.5 258 234.2 Q264 233.5 264 221 Z' }, glass);
      svg('line', { x1: 258, y1: 234.2, x2: 258, y2: 242 }, glass);
      svg('path', { d: 'M253 242.9 L263 242.9' }, glass);
    } else {
      svg('use', { href: `#luve-builder-person-${this.#uid}`, transform: `translate(${r2(referenceX + shift)} 0)` }, scene);
    }
    const note = svg('text', { x: r2(referenceX + shift), y: r2(labelSize * 1.9), 'text-anchor': 'middle', 'font-size': r2(labelSize * 0.86), class: 'luve-builder__label luve-builder__label--soft' }, scene);
    note.textContent = table ? 'Glass 22cm' : '165cm';

    const caption = this.querySelector('[data-builder-caption]');
    if (caption) {
      const scale = table ? 'Drawn to scale on a dining table, beside a 22cm wine glass.' : 'Drawn to scale beside a 165cm person.';
      caption.textContent = slots.length > 1 ? `${scale} Tap a tree to change it.` : scale;
    }
    this.#fresh = new Set();
  }

  /**
   * @param {Element} parent
   * @param {Slot} tree
   * @param {string[]} tones - base, highlight, shade, line
   * @param {number} x
   * @param {number} index
   * @param {number} pixelsPerCm
   */
  #drawTree(parent, tree, tones, x, index, pixelsPerCm) {
    const [base = '#B5AA9B', hi = '#CFC6B9', shade = '#8F8577', line = '#6E665B'] = tones;
    const id = `${this.#uid}-tree-${index}`;
    const defs = svg('defs', {}, parent);
    const gradient = (name, attributes, stops) => {
      const node = svg('linearGradient', { id: `${id}-${name}`, ...attributes }, defs);
      for (const [offset, colour, opacity = 1] of stops) svg('stop', { offset, 'stop-color': colour, 'stop-opacity': opacity }, node);
    };
    gradient('tier', { x1: 0, y1: 0, x2: 1, y2: 0 }, [[0, shade], [0.3, hi], [0.58, base], [1, shade]]);
    gradient('shade', { x1: 0, y1: 0, x2: 0, y2: 1 }, [[0, '#140E0A', 0.26], [0.32, '#140E0A', 0]]);
    gradient('facet', { x1: 0, y1: 0, x2: 0, y2: 1 }, [[0, line, 0.06], [0.6, line, 0.3], [1, line, 0.46]]);

    const place = svg('g', { transform: `translate(${r2(x)} 0)` }, parent);
    const art = svg('g', { class: `luve-builder__tree-art${this.#fresh.has(index) ? ' is-new' : ''}` }, place);
    const shape = svg('g', { transform: `scale(${tree.cm})` }, art);

    const width = 2 * halfFor(tree.model, tree.cm) * tree.cm * pixelsPerCm;
    const fins = finsFor(tree.model, tree.cm);
    const pleats = Math.max(Math.min(6, fins), Math.min(fins, Math.round(width / MIN_PLEAT_PX)));

    for (const tier of tierPaths(tree.model, pleats, tree.cm)) {
      const group = svg('g', {}, shape);
      svg('path', { d: tier.outline, fill: `url(#${id}-tier)` }, group);
      if (tier.shaded) svg('path', { d: tier.outline, fill: `url(#${id}-shade)` }, group);
      svg('path', { d: tier.facets, fill: `url(#${id}-facet)` }, group);
      if (tier.shadows) svg('path', { d: tier.shadows, fill: line, 'fill-opacity': 0.3 }, group);
      svg('path', { d: tier.pleats, class: 'luve-builder__pleats', stroke: hi }, group);
      if (tier.rim) svg('path', { d: tier.rim, class: 'luve-builder__rim', stroke: hi }, group);
    }
  }

  /** The saving percent a set of this many trees reaches, 0 when none. @param {number} count */
  #percentFor(count) {
    return this.#tiers.reduce((best, tier) => (count >= tier.min && tier.pct > best ? tier.pct : best), 0);
  }

  /**
   * What the set costs: the plain sum, the saving (rounded per tree, as Shopify's percentage discount is), and what is
   * left to pay.
   */
  #price() {
    const variants = this.#slots.map((tree) => this.#variant(tree));
    const full = variants.reduce((sum, variant) => sum + (variant?.price || 0), 0);
    const pct = this.#percentFor(variants.length);
    const saving = variants.reduce((sum, variant) => sum + Math.round(((variant?.price || 0) * pct) / 100), 0);
    return { variants, full, pct, saving, total: full - saving };
  }

  #renderSummary() {
    const { variants, full, pct, saving, total } = this.#price();
    const ready = variants.length > 0 && variants.every((variant) => variant && variant.available);
    const count = this.#slots.length;

    const counter = this.querySelector('[data-builder-count]');
    if (counter) counter.textContent = `Your set, ${count} tree${count === 1 ? '' : 's'}`;
    const totalText = this.querySelector('[data-builder-total]');
    if (totalText) totalText.textContent = money(total);
    const was = /** @type {HTMLElement | null} */ (this.querySelector('[data-builder-was]'));
    if (was) {
      was.textContent = money(full);
      was.hidden = saving === 0;
    }
    this.#renderSaving(count, pct, saving);

    // "Free shipping on every order" (Theme settings > Luvè delivery and shipping): every set ships free
    const freeAll = this.dataset.freeAll === 'true';
    const threshold = Number(this.dataset.threshold) || 0;
    const free = freeAll || (threshold > 0 && total >= threshold);
    const shipping = this.querySelector('[data-builder-shipping]');
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-builder-bar]'));
    if (shipping) {
      shipping.textContent = freeAll || threshold > 0 ? (free ? 'Your set ships free.' : `${money(threshold - total)} away from free shipping.`) : '';
      shipping.classList.toggle('is-free', free);
    }
    if (bar) bar.style.width = freeAll ? '100%' : threshold > 0 ? `${Math.min(100, (total / threshold) * 100)}%` : '0';

    // The pinned bar's short line: what the set saves and whether it ships free
    const mini = this.querySelector('[data-builder-mini]');
    if (mini) {
      const parts = [];
      if (saving) parts.push(`You save ${money(saving)}`);
      if (freeAll || threshold > 0) parts.push(free ? 'Ships free' : `${money(threshold - total)} to free shipping`);
      mini.textContent = parts.join(' · ');
    }

    const installments = /** @type {HTMLElement | null} */ (this.querySelector('[data-builder-installments]'));
    if (installments) {
      const { enabled, name = 'Afterpay', count: parts = 4, min = 100, max = 200000 } = this.#afterpay;
      const show = Boolean(enabled) && parts > 1 && total >= min && total <= max;
      installments.hidden = !show;
      installments.textContent = show ? `or ${parts} interest-free payments of ${money(Math.floor(total / parts))} with ${name}` : '';
    }

    if (!this.#busy && !this.#addedTimer) this.#setButton(ready ? this.#label : 'Not available yet', !ready);
  }

  get #label() {
    return this.dataset.label || 'Add the set';
  }

  /**
   * The saving line, the ladder of tiers, and a nudge to the next tier while it is in reach.
   *
   * @param {number} count
   * @param {number} pct
   * @param {number} saving
   */
  #renderSaving(count, pct, saving) {
    const line = /** @type {HTMLElement | null} */ (this.querySelector('[data-builder-saving]'));
    if (line) {
      line.hidden = saving === 0;
      line.textContent = saving ? `${this.#savingLabel}: ${pct}% off ${count} trees, you save ${money(saving)}` : '';
    }

    const ladder = /** @type {HTMLElement | null} */ (this.querySelector('[data-builder-ladder]'));
    if (ladder) {
      ladder.replaceChildren();
      ladder.hidden = !this.#tiers.length;
      for (const tier of this.#tiers) {
        if (tier.min > this.#max) continue;
        const reached = tier.pct === pct && count >= tier.min;
        const label = `${tier.min}${tier.min === this.#max || tier === this.#tiers[this.#tiers.length - 1] ? '+' : ''} trees: ${tier.pct}% off`;
        ladder.append(html('li', { class: `luve-builder__tier${reached ? ' is-reached' : ''}` }, label));
      }
    }

    const nudge = /** @type {HTMLElement | null} */ (this.querySelector('[data-builder-nudge]'));
    if (nudge) {
      const next = this.#tiers.find((tier) => tier.min > count && tier.pct > pct && tier.min <= this.#max);
      const more = next ? next.min - count : 0;
      nudge.hidden = !next;
      const text = nudge.querySelector('[data-builder-nudge-text]') || nudge;
      text.textContent = next ? `Add ${more} more tree${more === 1 ? '' : 's'} and save ${next.pct}% on the whole set.` : '';
    }
  }

  #onAdd() {
    if (this.#busy) return;
    const variants = this.#slots.map((tree) => this.#variant(tree));
    if (!variants.length || !variants.every((variant) => variant && variant.available)) return;

    /** @type {Map<number, number>} */
    const quantities = new Map();
    for (const variant of variants) if (variant) quantities.set(variant.id, (quantities.get(variant.id) || 0) + 1);
    const lines = [...quantities].map(([id, quantity]) => ({ id, quantity }));

    this.#busy = true;
    this.#setButton('Adding', true);
    this.#setStatus('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const detail = { source: 'luve-trio-builder', sourceId: this.#uid, itemCount: variants.length };
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        const cart = await this.#fetchCart();

        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: response.description, errors: response.errors },
            })
          );
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { ...detail, items: cart.items, didError: true },
          });
          this.#setStatus(response.description || response.message || 'The set could not be added.', true);
          this.#setButton(this.#label, false);
          return;
        }

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, sections: response.sections, didError: false },
        });
        this.#setButton('Added', true);
        this.#setStatus(`Your set of ${variants.length} is in your cart.`);
        this.#addedTimer = setTimeout(() => {
          this.#addedTimer = undefined;
          this.#renderSummary();
        }, ADDED_MS);
      })
      .catch((error) => {
        console.error(error);
        deferred.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
        );
        this.#setStatus('Something went wrong. Please try again.', true);
        this.#setButton(this.#label, false);
      })
      .finally(() => {
        this.#busy = false;
      });
  }

  /** The cart after the add: through Horizon's cart component when the page has one, otherwise /cart.js */
  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      // @ts-ignore Horizon's CartItemsComponent
      if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  /** @param {string} text @param {boolean} disabled */
  #setButton(text, disabled) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-builder-add]'));
    if (!button) return;
    button.textContent = text;
    button.disabled = disabled;
  }

  /** @param {string} text @param {boolean} [isError] */
  #setStatus(text, isError = false) {
    const status = this.querySelector('[data-builder-status]');
    if (!status) return;
    status.textContent = text;
    status.classList.toggle('is-error', isError);
  }
}

if (!customElements.get('luve-trio-builder')) {
  customElements.define('luve-trio-builder', LuveTrioBuilder);
}
